import IS_TOUCH_SUPPORTED from '@environment/touchSupport';
import AppSelectPeers, {SelectSearchPeerType} from '@components/appSelectPeers';
import PopupElement, {createPopup, PopupContext, PopupContextValue} from '@components/popups/indexTsx';
import I18n, {LangPackKey, i18n} from '@lib/langPack';
import {Modify} from '@types';
import {IsPeerType} from '@appManagers/appPeersManager';
import TransitionSlider from '@components/transition';
import appNavigationController, {NavigationItem} from '@components/appNavigationController';
import {ForumTopic} from '@layer';
import RowTsx from '@components/rowTsx';
import {wrapSolidComponent} from '@helpers/solid/wrapSolidComponent';
import wrapEmojiText from '@lib/richTextProcessor/wrapEmojiText';
import {avatarNew} from '@components/avatarNew';
import {makeMediaSize} from '@helpers/mediaSize';
import getDialogIndex from '@appManagers/utils/dialogs/getDialogIndex';
import {Middleware} from '@helpers/middleware';
import deferredPromise from '@helpers/cancellablePromise';
import {MOUNT_CLASS_TO} from '@config/debug';
import Modes from '@config/modes';
import findUpAttribute from '@helpers/dom/findUpAttribute';
import cancelEvent from '@helpers/dom/cancelEvent';
import rootScope from '@lib/rootScope';
import apiManagerProxy from '@lib/apiManagerProxy';
import createTopPeersList from '@components/topPeersList';
import findAndSplice from '@helpers/array/findAndSplice';
import FoldersTabs from '@components/foldersTabs';
import {observeResize} from '@components/resizeObserver';
import {render} from 'solid-js/web';
import {selectTarget} from '@components/horizontalMenu';
import {ScrollableContextValue} from '@components/scrollable2';
import whichChild from '@helpers/dom/whichChild';
import showLimitPopup from '@components/popups/limit';
import type DialogsStorage from '@lib/storages/dialogs';
import type MonoforumDialogsStorage from '@lib/storages/monoforumDialogs';
import wrapPeerTitle from '@components/wrappers/peerTitle';
import {Accessor, createSignal, JSX, Show, untrack, useContext} from 'solid-js';
import {ButtonIconTsx} from '@components/buttonIconTsx';
import fastSmoothScroll from '@helpers/fastSmoothScroll';
import {AppManagers} from '@lib/managers';
import {REAL_FOLDERS} from '@lib/appManagers/constants';

type PopupPickUserSelectedItem = {
  peerId: PeerId,
  threadId?: number,
  monoforumThreadId?: PeerId,
  key: string
};

export type PopupPickUserOptions = Modify<ConstructorParameters<typeof AppSelectPeers>[0], {
  appendTo?: never,
  managers?: never,
  onSelect?: (chosen: PopupPickUserSelectedItem[]) => Promise<void> | void,
  onClose?: () => void,
  onCloseAfterTimeout?: () => void,
  middleware?: never,
  titleLangKey: LangPackKey,
  initial?: PeerId[],
  prependPeerIds?: PeerId[],
  useTopics?: boolean,
  footerButtonProps?: Parameters<typeof PopupElement['FooterButton']>[0],
  footer?: (ctx: {multiSelect: Accessor<AppSelectPeers['multiSelect']>}) => JSX.Element,
  autoHeight?: boolean,
  showTopPeers?: boolean,
  btnConfirmOnEnter?: Parameters<typeof PopupElement>[0]['btnConfirmOnEnter'],
  containerProps?: Parameters<typeof PopupElement>[0]['containerProps']
}>;

async function wrapTopicRow({
  peerId,
  threadId,
  middleware
}: {
  peerId: PeerId,
  threadId: number,
  middleware: Middleware
}) {
  const size = makeMediaSize(32, 32);
  const title = threadId ? await wrapPeerTitle({peerId, threadId}) : i18n('AllMessages');
  let media: JSX.Element;
  if(threadId) {
    const avatar = avatarNew({
      peerId,
      threadId,
      middleware,
      size: size.width,
      wrapOptions: {
        middleware,
        textColor: 'primary-text-color',
        customEmojiSize: size
      }
    });
    await avatar.readyThumbPromise;
    media = avatar.node;
  } else {
    media = wrapEmojiText('💬');
  }

  const row = wrapSolidComponent(() => (
    <RowTsx
      clickable
      class="selector-forum-topic"
      classList={{'selector-forum-topic-all': !threadId}}
    >
      <RowTsx.Title>{title}</RowTsx.Title>
      <RowTsx.Media size="abitbigger">{media}</RowTsx.Media>
    </RowTsx>
  ), middleware);
  row.dataset.peerId = [peerId, threadId].filter(Boolean).join('_');
  return row;
}

export default function showPickUserPopup(options: PopupPickUserOptions) {
  let multiSelect: AppSelectPeers['multiSelect'];
  if(typeof(options.multiSelect) === 'boolean') {
    multiSelect = options.multiSelect ? 'enabled' : 'disabled';
  } else {
    multiSelect = options.multiSelect;
  }
  multiSelect ||= 'disabled';

  const _onSelect = options.onSelect;

  const [show, setShow] = createSignal(false);
  const [canGoBack, setCanGoBack] = createSignal(false);
  const [multiSelectMode, setMultiSelectMode] = createSignal<AppSelectPeers['multiSelect']>(multiSelect);

  let selector: AppSelectPeers;
  let forumSelector: AppSelectPeers | undefined;
  let transition: ReturnType<typeof TransitionSlider> | undefined;
  let forumNavigationItem: NavigationItem | undefined;
  let btnConfirm: HTMLButtonElement;
  let isMonoforum = false;
  const selected: Array<PopupPickUserSelectedItem> = [];

  let context: PopupContextValue;
  let managers: AppManagers;
  let middleware: Middleware;
  let night: boolean;

  const tabsContainer = document.createElement('div');
  tabsContainer.classList.add('tabs-container');

  let busy = false;
  const finalize = async() => {
    if(busy) {
      return;
    }

    busy = true;
    try {
      const res = _onSelect(selected);
      if(res instanceof Promise) {
        await res;
      }

      busy = false;
    } catch(err) {
      busy = false;
      throw err;
    }

    setShow(false);
  };

  const onSingleSelect = async(
    sel: AppSelectPeers,
    key: PeerId | string,
    adding: boolean
  ) => {
    key = '' + key;
    const [_peerId, _threadId] = key.split('_');
    const peerId = _peerId.toPeerId();
    const threadId = _threadId ? +_threadId : undefined;
    const item: PopupPickUserSelectedItem = {
      peerId,
      threadId: isMonoforum ? undefined : threadId,
      monoforumThreadId: isMonoforum ? threadId : undefined,
      key
    };

    if(adding) selected.push(item);
    else findAndSplice(selected, (item) => item.key === key);

    if((sel.multiSelect === 'enabled' && selected.length) || !selected.length) {
      return;
    }

    await finalize();
  };

  // The header button both reflects and drives the selector the user is currently looking at, so
  // every selector reports its mode back here — the peer list and, once opened, the topic list.
  const trackMultiSelectMode = (sel: AppSelectPeers) => {
    const originalSetMultiSelectMode = sel.setMultiSelectMode.bind(sel);
    sel.setMultiSelectMode = (mode) => {
      originalSetMultiSelectMode(mode);
      setMultiSelectMode(mode);
    };
    setMultiSelectMode(sel.multiSelect);
  };

  const enableMultiSelect = () => {
    (canGoBack() ? forumSelector : selector)?.setMultiSelectMode('enabled');
  };

  const onBackClick = () => {
    if(forumSelector) {
      const selected = forumSelector.getSelected();
      selector.removeBatch(selector.getSelected(), false);
      selector.setMultiSelectMode(forumSelector.multiSelect);
      selector.addInitial(selected);
    }

    transition?.(selector.container);
    if(forumNavigationItem) {
      appNavigationController.removeItem(forumNavigationItem);
      forumNavigationItem = undefined;
    }
    setCanGoBack(false);
  };

  const createForumSelector = async({
    peerId,
    placeholder,
    isMonoforum: _isMonoforum
  }: {
    peerId: PeerId,
    placeholder: LangPackKey,
    isMonoforum: boolean
  }) => {
    const fsMiddlewareHelper = middleware.create();
    const fsMiddleware = fsMiddlewareHelper.get();
    const deferred = deferredPromise<void>();
    let offsetIndex: number, lastQuery: string, firstRender = true;
    isMonoforum = _isMonoforum;
    const selected = selector.getSelected();
    const fs = forumSelector = new AppSelectPeers({
      middleware: fsMiddleware,
      appendTo: tabsContainer,
      managers,
      rippleEnabled: false,
      night,
      multiSelect,
      placeholder,
      peerType: ['custom'],
      onChange: (length, changes) => {
        changes.forEach(({key, add}) => onSingleSelect(fs, key, add));
      },
      getMoreCustom: async(q, middleware) => {
        if(lastQuery !== q) {
          offsetIndex = undefined;
          lastQuery = q;
        }

        const limit = 20;
        let result: Awaited<ReturnType<DialogsStorage['getDialogs']> | ReturnType<MonoforumDialogsStorage['getDialogs']>>;
        if(_isMonoforum) {
          result = await managers.monoforumDialogsStorage.getDialogs({
            parentPeerId: peerId,
            limit,
            offsetIndex
          });
        } else {
          result = await managers.dialogsStorage.getDialogs({
            query: q,
            filterId: peerId,
            limit,
            skipMigrated: true,
            offsetIndex
          });
        }

        if(!middleware()) {
          return;
        }

        offsetIndex = getDialogIndex(result.dialogs[result.dialogs.length - 1]);

        return {
          result: _isMonoforum ?
            result.dialogs.map((dialog) => dialog.peerId) as any :
            result.dialogs.map((forumTopic) => (forumTopic as ForumTopic.forumTopic).id),
          isEnd: result.isEnd
        };
      },
      renderResultsFunc: async(threadIds, append) => {
        const middleware = fs.middlewareHelperLoader.get();
        if(firstRender) {
          firstRender = false;

          if(apiManagerProxy.isBotforum(peerId)) {
            threadIds.unshift(undefined);
          }
        }

        const promises = threadIds.map((threadId) => {
          return wrapTopicRow({peerId, threadId, middleware});
        });
        const elements = await Promise.all(promises);
        if(!middleware()) {
          return;
        }

        elements.forEach((element) => {
          const sel = fs.selected.has(element.dataset.peerId);
          element.prepend(fs.checkbox(sel, undefined, element.querySelector<HTMLElement>('.peer-title, .row-title')));
        });
        fs.list[!append ? 'append' : 'prepend'](...elements);
      },
      // placeholderSizes: _isMonoforum ? undefined : {
      //   avatarSize: 32,
      //   avatarMarginRight: 72 - 32 - 17,
      //   marginVertical: 8,
      //   marginLeft: 17,
      //   lineMarginVertical: 11,
      //   gapVertical: 0,
      //   totalHeight: 48,
      //   statusWidth: 0,
      //   noSecondLine: true,
      //   night
      // },
      onFirstRender: () => {
        deferred.resolve();
      },
      noSearch: _isMonoforum && !selected.length
    });

    fs.setMultiSelectMode(selector.multiSelect);
    trackMultiSelectMode(fs);
    fs.addInitial(selected);
    fs.container.classList.add('tabs-tab');
    fs.container.middlewareHelper = fsMiddlewareHelper;
    if(_isMonoforum && fs.selectorSearch) {
      fs.selectorSearch.inputSearch.container.classList.add('hide');
    }

    await deferred;

    setCanGoBack(true);
    transition?.(fs.container);
    const navigationItem: NavigationItem = forumNavigationItem = {
      type: 'popup',
      onPop: () => {
        onBackClick();
      }
    };
    appNavigationController.pushItem(navigationItem);
    middleware.onClean(() => {
      isMonoforum = false;
      appNavigationController.removeItem(navigationItem);
    });
  };

  const createFolderTabs = (afterElement: HTMLElement) => {
    let menu: HTMLElement;
    let prevId = -1;
    const mount = document.createElement('div');
    mount.classList.add('popup-forward-folder-tabs-container', 'collapsable');
    afterElement.after(mount);
    let scrollableContext: ScrollableContextValue;

    const sectionContainer = selector.selectorSearch.section.container;
    const unobserve = observeResize(sectionContainer, () => {
      mount.style.top = sectionContainer.offsetHeight + 'px';
    });
    const lateMiddleware = untrack(() => context.lateMiddlewareHelper).get();
    lateMiddleware.onClean(unobserve);

    const deferred = deferredPromise<void>();

    const scrollToStart = (callback: () => void) => {
      let clicked = false;
      const onFinish = () => {
        if(!clicked) {
          clicked = true;
          callback();
        }
      };

      fastSmoothScroll({
        container: selector.scrollable.container,
        element: selector.section.container,
        position: 'start',
        getElementPosition: ({elementPosition}) => elementPosition -
          mount.offsetHeight -
          sectionContainer.offsetHeight,
        startCallback: ({duration, path}) => {
          if(path >= 0) onFinish();
          else setTimeout(onFinish, Math.max(0, duration / 2));
        }
      }).finally(callback);
    };

    const onTabClick = (target: HTMLElement, id: number) => {
      return selectTarget({
        target,
        id,
        tabs: menu,
        onClick: async() => {
          const filterId = +(target.dataset.filterId || 0);
          const available = REAL_FOLDERS.has(filterId) ||
            (await rootScope.managers.filtersStorage.isFilterIdAvailable(filterId) ?? true);
          if(!available) {
            showLimitPopup('folders');
            return false;
          }

          if(prevId !== -1) {
            await new Promise<void>((resolve) => scrollToStart(resolve));
          }

          selector.setFolderId(filterId);
          prevId = id;
        },
        prevId,
        scrollableX: scrollableContext
      });
    };

    const dispose = render(() => FoldersTabs({
      scrollableProps: {
        class: 'popup-forward-folder-tabs',
        scrollableProps: {
          contextRef: (ref) => scrollableContext = ref
        }
      },
      menuProps: {
        ref: (ref) => {
          menu = ref;
          queueMicrotask(async() => {
            const first = ref.firstElementChild as HTMLElement;
            if(first) await onTabClick(first, 0);
            deferred.resolve();
          });
        },
        onClick: (e) => {
          cancelEvent(e);
          const target = findUpAttribute(e.target, 'data-filter-id') as HTMLElement;
          if(!target) return;
          const id = whichChild(target);
          onTabClick(target, id);
        }
      },
      gradientProps: {
        color: 'background',
        className: 'popup-forward-folder-tabs-gradient'
      }
    }), mount);

    lateMiddleware.onClean(dispose);

    selector.onSearchChange = (query) => {
      mount.classList.toggle('is-collapsed', !!query);
    };

    return deferred;
  };

  function Inner() {
    context = useContext(PopupContext);
    managers = untrack(() => context.managers);
    middleware = untrack(() => context.middlewareHelper.get());
    night = untrack(() => context.night);

    selector = new AppSelectPeers({
      ...options,
      multiSelect,
      middleware,
      appendTo: tabsContainer,
      onChange: (length, changes) => {
        btnConfirm?.classList.toggle('is-visible', !!length);
        changes.forEach(({key, add}) => onSingleSelect(selector, key, add));
        options.onChange?.(length, changes);
      },
      onSelect: async(peerId, adding, e) => {
        let _isMonoforum = false;
        if(
          options.useTopics &&
          e.isTrusted &&
          (
            apiManagerProxy.isForum(peerId) ||
            apiManagerProxy.isBotforum(peerId) ||
            (_isMonoforum = (
              await managers.appPeersManager.canManageDirectMessages(peerId) &&
              await managers.appPeersManager.isMonoforum(peerId)
            ))
          )
        ) {
          createForumSelector({
            peerId,
            placeholder: options.placeholder,
            isMonoforum: _isMonoforum
          });
          return false;
        }
      },
      onFirstRender: () => {
        setShow(true);
        selector.checkForTriggers();

        if(!IS_TOUCH_SUPPORTED && selector.inputSearch) {
          selector.inputSearch.input.focus();
        }
      },
      noPlaceholder: true,
      rippleEnabled: false,
      avatarSize: 'abitbigger',
      managers,
      night,
      noInstantLoad: true,
      noSearch: options.noSearch
    });
    selector.container.classList.add('tabs-tab');

    trackMultiSelectMode(selector);

    const loadPromises: Promise<any>[] = [];
    if(options.showTopPeers) {
      const {group, promise} = createTopPeersList({
        middleware,
        className: 'popup-forward-top-peers collapsable',
        modifyPeers: (peers) => {
          findAndSplice(peers, (peer) => peer.id === rootScope.myId);
          peers.unshift({
            id: rootScope.myId,
            rating: 0
          });
          return peers;
        }
      });
      selector.heightContainer.before(group.container);

      loadPromises.push(createFolderTabs(group.container));

      const prevOnSearchChange = selector.onSearchChange;
      selector.onSearchChange = (query) => {
        group.container.classList.toggle('is-collapsed', !!query);
        prevOnSearchChange?.(query);
      };

      if(multiSelect !== 'disabled') promise.then(() => {
        const topPeerElements = group.list.querySelectorAll<HTMLElement>('.chatlist-chat');

        topPeerElements.forEach((el) => {
          const peerId = el.dataset.peerId.toPeerId();
          const sel = selector.selected.has(peerId);
          el.prepend(selector.checkbox(sel, 'white'));
        });
      });
    }

    if(options.initial) {
      selector.addInitial(options.initial);
    }
    selector.container.classList.add('active');

    if(options.useTopics) {
      transition = TransitionSlider({
        content: tabsContainer,
        type: 'tabs',
        transitionTime: 150,
        animateFirst: false,
        onTransitionEnd: (id) => {
          if(!id) {
            (Array.from(tabsContainer.children) as HTMLElement[]).forEach((el) => {
              if(el !== selector.container) {
                (el as any).middlewareHelper?.destroy();
                el.remove();
              }
            });
          } else {
            selector.clearInput();
          }
        }
      });
      transition(selector.container);
    }

    Promise.all(loadPromises).then(() => {
      selector.loadFirst();
    });

    return tabsContainer;
  }

  function FooterInner(): JSX.Element {
    if(options.footer) {
      return options.footer({multiSelect: multiSelectMode});
    }

    return (
      <PopupElement.FooterButton
        confirm
        langKey={Modes.a11y ? 'Next' : undefined}
        callback={() => {
          finalize();
        }}
        {...(options.footerButtonProps || {})}
      />
    );
  }

  createPopup(() => {
    return (
      <PopupElement
        class="popup-forward"
        containerClass={options.autoHeight ? 'popup-forward-auto-height' : undefined}
        containerProps={options.containerProps}
        closable
        show={show()}
        onClose={() => {
          options.onClose?.();
        }}
        onCloseAfterTimeout={() => {
          selector?.destroy();
          selector = undefined;
          options.onCloseAfterTimeout?.();
        }}
        btnConfirmOnEnter={options.btnConfirmOnEnter}
      >
        <PopupElement.Header>
          <PopupElement.CloseButton
            canGoBack={canGoBack()}
            onBackClick={onBackClick}
          />
          <PopupElement.Title title={options.titleLangKey} />
          {/* 'hidden' is the only mode where multiselect is available but off — 'disabled' has none
              to offer and 'enabled' is already selecting, so the button belongs to that state alone */}
          <Show when={multiSelectMode() === 'hidden'}>
            <ButtonIconTsx
              icon="checkround"
              class="popup-forward-multiselect"
              aria-label={I18n.format('Message.Context.Select', true)}
              onClick={enableMultiSelect}
            />
          </Show>
        </PopupElement.Header>
        <PopupElement.Body>
          <Inner />
        </PopupElement.Body>
        <Show when={multiSelect !== 'disabled' || options.footer}>
          <PopupElement.Footer class="popup-forward-footer" floating>
            <FooterInner />
          </PopupElement.Footer>
        </Show>
      </PopupElement>
    );
  });

  return {
    get selector() {
      return selector;
    },
    get middleware() {
      return middleware;
    },
    hide() {
      setShow(false);
    },
    finalize
  };
}

export async function showPickUser2Popup<T extends boolean = false>({
  peerType,
  filterPeerTypeBy,
  chatRightsActions,
  multiSelect,
  limit,
  limitCallback,
  titleLangKey,
  placeholder,
  exceptSelf
}: {
  peerType?: SelectSearchPeerType[],
  filterPeerTypeBy: AppSelectPeers['filterPeerTypeBy'],
  chatRightsActions?: PopupPickUserOptions['chatRightsActions'],
  placeholder?: LangPackKey,
  multiSelect?: T,
  limit?: number,
  limitCallback?: () => void,
  titleLangKey?: LangPackKey,
  exceptSelf?: boolean
}) {
  return new Promise<T extends false ? PeerId : PeerId[]>((resolve, reject) => {
    let resolved = false;
    const popup = showPickUserPopup({
      peerType,
      placeholder: placeholder || 'SelectChat',
      onSelect: (chosen) => {
        // The footer button stays clickable with an empty selection — treat
        // that as a cancel (onClose rejects) instead of resolving with [].
        if(!chosen.length) {
          return;
        }

        resolved = true;
        resolve((multiSelect ? chosen.map((c) => c.peerId) : chosen[0].peerId) as any);
      },
      multiSelect,
      // The batch is confirmed via the floating footer button, which needs a
      // label — an unlabeled FooterButton renders as an empty bar.
      footerButtonProps: multiSelect ? {langKey: 'Send'} : undefined,
      filterPeerTypeBy,
      chatRightsActions,
      titleLangKey,
      exceptSelf,
      onClose: () => {
        if(!resolved) {
          reject();
        }
      }
    });

    if(limit) {
      popup.selector?.setLimit(limit, limitCallback);
    }
  });
}

export async function showPickUser3Popup(
  types: Parameters<typeof AppSelectPeers['convertPeerTypes']>[0] = ['users', 'bots', 'groups', 'channels'],
  chatRightsActions?: PopupPickUserOptions['chatRightsActions']
) {
  if(!Array.isArray(types)) {
    types = [];
  }

  const filterPeerTypeBy: IsPeerType[] = AppSelectPeers.convertPeerTypes(types);
  const peerType: SelectSearchPeerType[] = ['dialogs'];
  if(types.includes('users')) peerType.push('contacts');

  if(!filterPeerTypeBy.length) {
    throw undefined;
  }

  return showPickUser2Popup({
    peerType,
    filterPeerTypeBy,
    chatRightsActions,
    titleLangKey: 'ShareWith'
  });
}

/**
 * A picker footer that offers the link until a recipient is ticked. Confirming
 * an empty selection is a dead button, and the link itself is what someone who
 * opened the picker and picked nobody still wants — so that is what the button
 * becomes. Shared by the conference invite picker and every `shareUrlToPeers`
 * batch; both feed the returned pair straight into their picker options.
 */
export function createCopyLinkFooter(options: {
  confirmLangKey: LangPackKey,
  copy: () => void,
  confirm: () => void
}): Pick<PopupPickUserOptions, 'footer' | 'onChange'> {
  const [selectedCount, setSelectedCount] = createSignal(0);

  return {
    onChange: (length) => setSelectedCount(length),
    footer: () => (
      <PopupElement.FooterButton
        confirm
        langKey={selectedCount() ? options.confirmLangKey : 'CopyLink'}
        callback={() => {
          if(selectedCount()) {
            options.confirm();
            return;
          }

          options.copy();
          // Copying is not done with the picker — a recipient may still follow.
          return false;
        }}
      />
    )
  };
}

export function showSharingPickerPopup(options: {
  onSelect: PopupPickUserOptions['onSelect'],
  chatRightsActions?: PopupPickUserOptions['chatRightsActions'],
  excludeMonoforums?: PopupPickUserOptions['excludeMonoforums'],
  excludeBotforums?: PopupPickUserOptions['excludeBotforums'],
  placeholder?: LangPackKey,
  selfPresence?: LangPackKey,
  // Multi-select toggle for the picker. `true` ⇒ checkboxes, user confirms a
  // batch; `false` (default) ⇒ pick-and-send immediately. Pass-through to
  // AppSelectPeers' own `multiSelect` enum if a non-boolean is needed.
  multiSelect?: PopupPickUserOptions['multiSelect'],
  // Replace the floating confirm button — `shareUrlToPeers` swaps it for a
  // "copy the link" action while nothing is picked.
  footer?: PopupPickUserOptions['footer'],
  onChange?: PopupPickUserOptions['onChange'],
  onCloseAfterTimeout?: () => void,
  onClose?: () => void
}) {
  options.chatRightsActions ??= ['send_plain'];
  options.placeholder ??= 'ShareModal.Search.Placeholder';
  options.selfPresence ??= 'ChatYourSelf';
  return showPickUserPopup({
    titleLangKey: 'ShareWith',
    showTopPeers: true,
    // Default the floating footer button's label to "Send" — without this the
    // PopupElement.FooterButton renders as an empty purple bar in multi-select
    // mode (single-select callers complete via row click and never see it).
    footerButtonProps: {langKey: 'Send'},
    ...options,
    peerType: ['dialogs', 'contacts']
  });
}

export function showSharingPicker2Popup(options?: Modify<Parameters<typeof showSharingPickerPopup>[0], {onSelect?: never}>) {
  return new Promise<{peerId: PeerId, threadId?: number, monoforumThreadId?: PeerId}>((resolve, reject) => {
    let resolved = false;
    showSharingPickerPopup({
      ...(options || {}),
      onSelect: (chosen) => {
        resolved = true;
        resolve(chosen[0]);
      },
      onClose: () => {
        if(!resolved) {
          reject();
        }
      }
    });
  });
}

export function showReplyPickerPopup(options: Pick<PopupPickUserOptions, 'excludeBotforums' | 'excludeMonoforums'> = {}) {
  return showSharingPicker2Popup({
    placeholder: 'ReplyToDialog',
    selfPresence: 'SavedMessagesInfoQuote',
    ...options
  });
}

export function showContactPickerPopup() {
  return new Promise<PeerId>((resolve, reject) => {
    let resolved = false;
    showPickUserPopup({
      titleLangKey: 'Contacts',
      peerType: ['contacts'],
      placeholder: 'Search',
      onSelect: (chosen) => {
        resolved = true;
        resolve(chosen[0].peerId);
      },
      onClose: () => {
        if(!resolved) {
          reject();
        }
      }
    });
  });
}
